import { computed, nextTick, onScopeDispose, shallowRef, watch, type Ref } from 'vue';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import { useDentalImageDisplayStore } from './imageDisplayStore';
import { extractPlane } from './imageEnhancement';
import type { EnhancementRequest } from './imageEnhancement.worker';
import type { Maybe } from '@/src/types';

/** Per-view bounded display buffers. Never replaces the canonical image cache. */
export function useImageEnhancement(viewId: Ref<string>, source: Ref<Maybe<vtkImageData>>, axis: Ref<number>, slice: Ref<number>, width: Ref<number>, level: Ref<number>, enabled: Ref<boolean>) {
  const display = useDentalImageDisplayStore();
  const enhanced = shallowRef<vtkImageData>();
  let worker: Worker | undefined, generation = 0, disposed = false;
  let inFlight = false, pending: (() => void) | undefined;
  function release() { const old = enhanced.value; enhanced.value = undefined; if (old) nextTick(() => old.delete()); }
  const active = computed(() => enabled.value && display.active);
  watch([source, axis, slice, width, level, active, () => JSON.stringify(display.settings)], () => {
    const id = ++generation; release(); pending = undefined; display.error = '';
    display.processing[viewId.value] = false;
    const image = source.value;
    if (!active.value || !image) return;
    const a = axis.value, index = Math.round(slice.value), dims = image.getDimensions();
    if (a < 0 || a > 2 || index < 0 || index >= dims[a]) return;
    display.processing[viewId.value] = true;
    // Coalesce rapid scrolling/slider changes: at most one in-flight slice and
    // one latest request per view, never queue or copy the whole scan.
    const send = () => {
      if (disposed || id !== generation) return;
      const plane = extractPlane(image.getPointData().getScalars().getData(), dims, a, index);
      const request: EnhancementRequest = { id, values: plane.values, width: plane.width, height: plane.height, windowWidth: width.value, level: level.value, settings: { ...display.settings } };
      if (!worker) {
        worker = new Worker(new URL('./imageEnhancement.worker.ts', import.meta.url), { type: 'module' });
        worker.onerror = () => { worker?.terminate(); worker = undefined; inFlight = false; pending = undefined; display.processing[viewId.value] = false; display.error = '影像增强失败，已显示原始灰度。请重试或重置。'; };
      }
      worker.onmessage = ({ data }: MessageEvent<{ id: number; pixels: Uint8Array }>) => {
        inFlight = false;
        if (!disposed && data.id === generation) {
          const output = vtkImageData.newInstance(); output.setDimensions(plane.dimensions); output.setSpacing(image.getSpacing()); output.setDirection(image.getDirection());
          const ijk: [number, number, number] = [0, 0, 0]; ijk[a] = index;
          output.setOrigin([...image.indexToWorld(ijk)] as [number, number, number]);
          const scalars = vtkDataArray.newInstance({ numberOfComponents: 1, values: data.pixels }); scalars.setRange({ min: 0, max: 255 }, 0); output.getPointData().setScalars(scalars);
          enhanced.value = output; display.processing[viewId.value] = false;
        }
        const latest = pending; pending = undefined; latest?.();
      };
      inFlight = true; worker.postMessage(request, [plane.values.buffer]);
    };
    if (inFlight) pending = send; else send();
  }, { immediate: true, flush: 'post' });
  onScopeDispose(() => { disposed = true; generation++; worker?.terminate(); delete display.processing[viewId.value]; release(); });
  // While a new slice is computing, show its current original plane instead
  // of a stale enhanced slice under the newly positioned labels.
  const mapperInput = computed(() => active.value && enhanced.value ? enhanced.value : source.value);
  const encoded = computed(() => active.value && !!enhanced.value);
  return { mapperInput, encoded };
}
